import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import ReactGA from "react-ga4";
import ReactPixel from "react-facebook-pixel";

import SanitizedHTML from "../../../../components/HTML/SanitizedHTML";

import PaymentForm from "../../../../components/version/3/Checkout/PaymentForm";
import OrderSummary from "../../../../components/version/3/Checkout/OrderSummary";
import PriceSummary from "../../../../components/version/3/Checkout/PriceSummary";
import Loading from "../../../../components/version/3/Checkout/Loading";

import {
  getSelectedLogo,
  getFeatureSymbolStyle,
  getPageCss,
} from "../../../../utils/CheckoutPage/pageStyleUtils";
import {
  loadGoogleFont,
  getSelectedFontFamily,
} from "../../../../utils/CheckoutPage/fontUtils";
import { injectScripts } from "../../../../utils/injectScripts";

import "./CheckoutPage.css";

import {
  DEFAULT_TESTIMONIAL_BACKGROUND_COLOR,
  DEFAULT_TESTIMONIAL_BORDER_COLOR,
  DEFAULT_ADDITIONAL_CONTENT_BACKGROUND_COLOR,
  DEFAULT_ADDITIONAL_CONTENT_BORDER_COLOR,
  DEFAULT_SUB_FOOTER_CONTENT_BACKGROUND_COLOR,
  DEFAULT_SUB_FOOTER_CONTENT_BORDER_COLOR,
  DEFAULT_FEATURE_SYMBOL,
} from "../../../../utils/CheckoutPage/constants";

// ============================================================
// HELPERS
// ============================================================

const normalizeCouponInput = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^\w-]/g, "")
    .slice(0, 64);

const getCouponCodeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);

  return normalizeCouponInput(params.get("coupon_code"));
};

const normalizeLocation = (location = {}) => ({
  country_iso2: String(location?.country_iso2 || "")
    .trim()
    .toUpperCase(),

  state: String(location?.state || "").trim(),

  postal_code: String(location?.postal_code || "")
    .trim()
    .toUpperCase(),
});

const getLocationKey = (location) => {
  const normalized = normalizeLocation(location);

  return [
    normalized.country_iso2,
    normalized.state,
    normalized.postal_code,
  ].join("|");
};

const getApiErrorMessage = (result, fallback) =>
  result?.error?.message || result?.errors?.[0]?.message || fallback;

const getEverflowTrackingParams = () => {
  const params = new URLSearchParams(window.location.search);

  const ef_transaction_id = String(
    params.get("_ef_transaction_id") || "",
  ).trim();

  const oid = String(params.get("oid") || "").trim();

  const affid = String(params.get("affid") || "").trim();

  return {
    ef_transaction_id,
    oid,
    affid,
  };
};

// ============================================================
// FREE TRIAL HELPERS
// ============================================================

const pickCouponForItem = (item, coupons) => {
  const list = Array.isArray(coupons) ? coupons : [];

  const itemPlanId = String(
    item?._id || item?.plan?._id || item?.price_id || "",
  );

  if (!itemPlanId) {
    return null;
  }

  return (
    list.find(
      (coupon) =>
        String(coupon?.plan?._id || coupon?.plan || "") === itemPlanId,
    ) || null
  );
};

// ============================================================
// TRACKING HELPERS
// ============================================================

const getTrackingConfig = (payload) => {
  const fb_pixel = String(payload?.trackings?.fb_pixel || "").trim();

  const fb_pixel_test_code = String(
    payload?.trackings?.fb_pixel_test_code || "",
  ).trim();

  const google_analytics_id = String(
    payload?.trackings?.google_analytics_id || "",
  ).trim();

  return {
    fb_pixel,
    fb_pixel_test_code,
    google_analytics_id,
  };
};

// ============================================================
// CUSTOM CHECKOUT SCRIPTS
// ============================================================

const getEnabledCheckoutScripts = (payload) => {
  const scripts = Array.isArray(payload?.checkout_page_scripts)
    ? payload.checkout_page_scripts
    : [];

  return scripts.filter(
    (item) =>
      item?.is_enabled === true && String(item?.snippet || "").trim() !== "",
  );
};

// ============================================================
// FETCH PRICE PLAN
// ============================================================

const fetchPlanForCheckout = async (store_slug, price_slug, signal) => {
  const endpointUrl = `${process.env.REACT_APP_API_ENDPOINT}/api/v2/stores/${store_slug}/price-plans/${price_slug}`;

  const url = new URL(endpointUrl);

  const couponCode = getCouponCodeFromUrl();

  if (couponCode) {
    url.searchParams.set("coupon_code", couponCode);
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    signal,
  });

  let result = null;

  try {
    result = await response.json();
  } catch (error) {
    result = null;
  }

  if (!response.ok) {
    throw new Error(
      getApiErrorMessage(
        result,
        "Something went wrong. Please try again later.",
      ),
    );
  }

  if (!result?.payloads) {
    throw new Error("Price plan data not found.");
  }

  return result.payloads;
};

// ============================================================
// RECALCULATE PRICING
// ============================================================

const recalculatePricing = async ({
  store_slug,
  price_slug,
  country_iso2 = "",
  state = "",
  postal_code = "",
  coupon_code = "",
  signal,
}) => {
  const params = new URLSearchParams();

  // Always include all four parameters.
  // Empty location values must NOT prevent recalculation.

  params.set("country_iso2", country_iso2 || "");
  params.set("state", state || "");
  params.set("postal_code", postal_code || "");
  params.set("coupon_code", coupon_code || "");

  const url = `${process.env.REACT_APP_API_ENDPOINT}/api/v2/stores/${store_slug}/price-plans/${price_slug}/recalculate?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-store",
    },
    cache: "no-store",
    signal,
  });

  let result = null;

  try {
    result = await response.json();
  } catch (error) {
    result = null;
  }

  if (!response.ok) {
    throw new Error(
      getApiErrorMessage(result, "Unable to recalculate pricing."),
    );
  }

  if (!result?.payloads) {
    throw new Error("Recalculated pricing data not found.");
  }

  return result.payloads;
};

// ============================================================
// CHECKOUT PAGE
// ============================================================

const CheckoutPage = () => {
  const { store, price_slug } = useParams();

  const store_slug = String(store || "").trim();
  const price_slug2 = String(price_slug || "").trim();

  // ============================================================
  // EVERFLOW TRACKING
  // ============================================================

  const everflowTrackingParams = getEverflowTrackingParams();

  // ============================================================
  // INITIAL LOADING
  // ============================================================

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState(null);

  // ============================================================
  // PRICING
  // ============================================================

  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [pricingSnapshot, setPricingSnapshot] = useState(null);

  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculationError, setRecalculationError] = useState("");

  // ============================================================
  // CUSTOMER LOCATION
  // ============================================================

  const [customerLocation, setCustomerLocation] = useState({
    country_iso2: "",
    state: "",
    postal_code: "",
  });

  const [locationTouched, setLocationTouched] = useState(false);

  // ============================================================
  // COUPON
  // ============================================================

  const [couponCode, setCouponCode] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");

  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const [couponError, setCouponError] = useState("");
  const [couponMessage, setCouponMessage] = useState("");

  // ============================================================
  // REQUEST MANAGEMENT
  // ============================================================

  const recalculationAbortRef = useRef(null);
  const recalculationRequestRef = useRef(0);

  const customerLocationRef = useRef({
    country_iso2: "",
    state: "",
    postal_code: "",
  });

  const appliedCouponRef = useRef("");

  const locationTouchedRef = useRef(false);

  // ============================================================
  // TRACKING REFERENCES
  // ============================================================

  const pixelInitKeyRef = useRef("");
  const gaInitKeyRef = useRef("");
  const trackedViewKeyRef = useRef("");

  // Location for which pricing was most recently calculated.
  const pricedLocationRef = useRef("");

  // ============================================================
  // CANCEL RECALCULATION
  // ============================================================

  const cancelRecalculation = useCallback(() => {
    recalculationRequestRef.current += 1;

    recalculationAbortRef.current?.abort();

    recalculationAbortRef.current = null;
  }, []);

  // ============================================================
  // START RECALCULATION REQUEST
  // ============================================================

  const createRecalculationRequest = useCallback(() => {
    cancelRecalculation();

    const controller = new AbortController();

    recalculationAbortRef.current = controller;

    return {
      controller,
      requestId: recalculationRequestRef.current,
    };
  }, [cancelRecalculation]);

  // ============================================================
  // CHECK CURRENT REQUEST
  // ============================================================

  const isCurrentRequest = useCallback((requestId, controller) => {
    return (
      !controller.signal.aborted &&
      requestId === recalculationRequestRef.current
    );
  }, []);

  // ============================================================
  // UPDATE PRICING
  // ============================================================

  const updatePricing = useCallback((payloads) => {
    setPriceBreakdown(payloads?.price_breakdown ?? null);

    setPricingSnapshot({
      price_breakdown: payloads?.price_breakdown ?? null,

      next_price_breakdown: payloads?.next_price_breakdown ?? null,

      bundles: Array.isArray(payloads?.bundles) ? payloads.bundles : [],

      coupons: Array.isArray(payloads?.coupons) ? payloads.coupons : [],
    });
  }, []);

  // ============================================================
  // LOAD CHECKOUT CONTENT
  // ============================================================

  useEffect(() => {
    let mounted = true;

    const controller = new AbortController();

    const loadCheckoutContent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        cancelRecalculation();

        const urlCoupon = getCouponCodeFromUrl();

        setCouponCode(urlCoupon);
        setAppliedCouponCode("");

        appliedCouponRef.current = "";

        setCouponError("");
        setCouponMessage("");

        setIsApplyingCoupon(false);
        setIsRecalculating(false);
        setRecalculationError("");

        const pricePlan = await fetchPlanForCheckout(
          store_slug,
          price_slug2,
          controller.signal,
        );

        if (!mounted) {
          return;
        }

        setFormData(pricePlan);

        updatePricing(pricePlan);

        // ========================================================
        // VALIDATE URL COUPON
        // ========================================================

        const returnedCoupons = Array.isArray(pricePlan?.coupons)
          ? pricePlan.coupons
          : [];

        if (urlCoupon) {
          if (returnedCoupons.length > 0) {
            appliedCouponRef.current = urlCoupon;

            setAppliedCouponCode(urlCoupon);
            setCouponMessage("Coupon applied.");
          } else {
            appliedCouponRef.current = "";

            setAppliedCouponCode("");
            setCouponError("Invalid or expired coupon code.");
          }
        }

        // ========================================================
        // RESET LOCATION
        // ========================================================

        const emptyLocation = {
          country_iso2: "",
          state: "",
          postal_code: "",
        };

        customerLocationRef.current = emptyLocation;

        setCustomerLocation(emptyLocation);

        locationTouchedRef.current = false;

        setLocationTouched(false);

        pricedLocationRef.current = "";
      } catch (err) {
        if (!mounted || controller.signal.aborted) {
          return;
        }

        console.error("Failed to load checkout content:", err);

        setError(err.message || "Unable to load checkout page.");
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadCheckoutContent();

    return () => {
      mounted = false;

      controller.abort();

      cancelRecalculation();
    };
  }, [store_slug, price_slug2, cancelRecalculation, updatePricing]);

  // ============================================================
  // CUSTOMER LOCATION CHANGE
  // ============================================================

  const handleLocationChange = useCallback(
    (location, options = {}) => {
      const normalizedLocation = normalizeLocation(location);

      const previousLocation = customerLocationRef.current;

      const previousKey = getLocationKey(previousLocation);
      const nextKey = getLocationKey(normalizedLocation);

      if (options.isUserInitiated) {
        locationTouchedRef.current = true;

        setLocationTouched(true);
      }

      if (previousKey === nextKey) {
        return;
      }

      customerLocationRef.current = normalizedLocation;

      setCustomerLocation(normalizedLocation);

      // A previous request must not update pricing for an old location.
      cancelRecalculation();

      setIsRecalculating(false);
      setIsApplyingCoupon(false);
      setRecalculationError("");
    },
    [cancelRecalculation],
  );

  // ============================================================
  // AUTOMATIC LOCATION RECALCULATION
  // ============================================================

  useEffect(() => {
    if (!formData || !locationTouched) {
      return;
    }

    const location = normalizeLocation(customerLocation);

    const locationKey = getLocationKey(location);

    // No need to repeat a calculation for the same location.
    if (locationKey === pricedLocationRef.current) {
      return;
    }

    // Wait until a country is selected for automatic recalculation.
    // Coupon application/removal does NOT have this restriction.
    if (!location.country_iso2) {
      return;
    }

    const needsStatePostal =
      location.country_iso2 === "US" || location.country_iso2 === "CA";

    // For US/CA, wait until the customer finishes the required fields.
    if (
      needsStatePostal &&
      (!location.state || location.postal_code.length < 3)
    ) {
      return;
    }

    const debounceMs = needsStatePostal ? 800 : 300;

    const timer = setTimeout(async () => {
      const { controller, requestId } = createRecalculationRequest();

      setIsRecalculating(true);
      setRecalculationError("");

      try {
        const payloads = await recalculatePricing({
          store_slug,
          price_slug: price_slug2,

          ...location,

          coupon_code: appliedCouponRef.current,

          signal: controller.signal,
        });

        if (!isCurrentRequest(requestId, controller)) {
          return;
        }

        updatePricing(payloads);

        pricedLocationRef.current = locationKey;

        // ======================================================
        // VERIFY APPLIED COUPON
        // ======================================================

        if (appliedCouponRef.current) {
          const returnedCoupons = Array.isArray(payloads?.coupons)
            ? payloads.coupons
            : [];

          if (!returnedCoupons.length) {
            appliedCouponRef.current = "";

            setAppliedCouponCode("");

            setCouponError(
              "The previously applied coupon is not valid for this billing location.",
            );

            setCouponMessage("");
          }
        }
      } catch (err) {
        if (!isCurrentRequest(requestId, controller)) {
          return;
        }

        console.error("Failed to recalculate pricing:", err);

        setRecalculationError(err.message || "Unable to recalculate pricing.");
      } finally {
        if (isCurrentRequest(requestId, controller)) {
          setIsRecalculating(false);
        }
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [
    formData,
    customerLocation,
    locationTouched,
    store_slug,
    price_slug2,
    createRecalculationRequest,
    isCurrentRequest,
    updatePricing,
  ]);

  // ============================================================
  // APPLY COUPON
  // ============================================================

  const applyCoupon = async () => {
    if (!formData || isApplyingCoupon || isRecalculating) {
      return;
    }

    const normalizedCoupon = normalizeCouponInput(couponCode);

    setCouponCode(normalizedCoupon);

    setCouponError("");
    setCouponMessage("");

    if (!normalizedCoupon) {
      setCouponError("Please enter a coupon code.");
      return;
    }

    if (normalizedCoupon === appliedCouponRef.current) {
      return;
    }

    // ==========================================================
    // LOCATION IS OPTIONAL
    // ==========================================================

    const location = normalizeLocation(customerLocationRef.current);

    const locationKey = getLocationKey(location);

    const { controller, requestId } = createRecalculationRequest();

    setIsApplyingCoupon(true);
    setIsRecalculating(true);
    setRecalculationError("");

    try {
      const payloads = await recalculatePricing({
        store_slug,
        price_slug: price_slug2,

        ...location,

        coupon_code: normalizedCoupon,

        signal: controller.signal,
      });

      if (!isCurrentRequest(requestId, controller)) {
        return;
      }

      const returnedCoupons = Array.isArray(payloads?.coupons)
        ? payloads.coupons
        : [];

      // ========================================================
      // INVALID COUPON
      // ========================================================

      if (!returnedCoupons.length) {
        setCouponError("Invalid or expired coupon code.");

        // Preserve the previous valid coupon and pricing.
        return;
      }

      // ========================================================
      // VALID COUPON
      // ========================================================

      appliedCouponRef.current = normalizedCoupon;

      setAppliedCouponCode(normalizedCoupon);

      setCouponMessage("Coupon applied.");

      updatePricing(payloads);

      pricedLocationRef.current = locationKey;
    } catch (err) {
      if (!isCurrentRequest(requestId, controller)) {
        return;
      }

      console.error("Failed to apply coupon:", err);

      setCouponError(err.message || "Unable to apply coupon.");
    } finally {
      if (isCurrentRequest(requestId, controller)) {
        setIsApplyingCoupon(false);
        setIsRecalculating(false);
      }
    }
  };

  // ============================================================
  // REMOVE COUPON
  // ============================================================

  const removeCoupon = async () => {
    if (
      !formData ||
      !appliedCouponRef.current ||
      isApplyingCoupon ||
      isRecalculating
    ) {
      return;
    }

    // ==========================================================
    // LOCATION IS OPTIONAL
    // ==========================================================

    const location = normalizeLocation(customerLocationRef.current);

    const locationKey = getLocationKey(location);

    const { controller, requestId } = createRecalculationRequest();

    setIsApplyingCoupon(true);
    setIsRecalculating(true);

    setCouponError("");
    setCouponMessage("");
    setRecalculationError("");

    try {
      const payloads = await recalculatePricing({
        store_slug,
        price_slug: price_slug2,

        ...location,

        coupon_code: "",

        signal: controller.signal,
      });

      if (!isCurrentRequest(requestId, controller)) {
        return;
      }

      // ========================================================
      // UPDATE PRICING
      // ========================================================

      updatePricing(payloads);

      pricedLocationRef.current = locationKey;

      // ========================================================
      // CLEAR COUPON
      // ========================================================

      appliedCouponRef.current = "";

      setAppliedCouponCode("");
      setCouponCode("");

      setCouponMessage("Coupon removed.");
    } catch (err) {
      if (!isCurrentRequest(requestId, controller)) {
        return;
      }

      console.error("Failed to remove coupon:", err);

      setCouponError(err.message || "Unable to remove coupon.");
    } finally {
      if (isCurrentRequest(requestId, controller)) {
        setIsApplyingCoupon(false);
        setIsRecalculating(false);
      }
    }
  };

  // ============================================================
  // INJECT CUSTOM CHECKOUT SCRIPTS
  // ============================================================

  useEffect(() => {
    if (!formData) {
      return;
    }

    const enabledScripts = getEnabledCheckoutScripts(formData);

    if (!enabledScripts.length) {
      return;
    }

    const cleanup = injectScripts(enabledScripts);

    return () => {
      if (typeof cleanup === "function") {
        cleanup();
      }
    };
  }, [formData]);

  // ============================================================
  // META PIXEL / GOOGLE ANALYTICS TRACKING
  // ============================================================

  useEffect(() => {
    if (!formData) {
      return;
    }

    const enabledScripts = getEnabledCheckoutScripts(formData);

    // ==========================================================
    // CUSTOM TRACKING TAKES PRIORITY
    // ==========================================================

    if (enabledScripts.length > 0) {
      return;
    }

    // ==========================================================
    // TRACKING CONFIGURATION
    // ==========================================================

    const tracking = getTrackingConfig(formData);

    const fbPixelId = tracking.fb_pixel;
    const fbPixelTestCode = tracking.fb_pixel_test_code;
    const gaId = tracking.google_analytics_id;

    // ==========================================================
    // PREVENT DUPLICATE TRACKING
    // ==========================================================

    const viewKey = [
      store_slug,
      price_slug2,
      formData?._id || "",
      fbPixelId,
      gaId,
    ].join("|");

    if (trackedViewKeyRef.current === viewKey) {
      return;
    }

    trackedViewKeyRef.current = viewKey;

    // ==========================================================
    // META PIXEL
    // ==========================================================

    if (fbPixelId) {
      const pixelInitKey = `${fbPixelId}::${fbPixelTestCode}`;

      if (pixelInitKeyRef.current !== pixelInitKey) {
        const pixelOptions = fbPixelTestCode
          ? { testEventCode: fbPixelTestCode }
          : undefined;

        ReactPixel.init(fbPixelId, {}, pixelOptions);

        pixelInitKeyRef.current = pixelInitKey;
      }

      ReactPixel.track("InitiateCheckout");
    }

    // ==========================================================
    // GOOGLE ANALYTICS 4
    // ==========================================================

    if (gaId) {
      if (gaInitKeyRef.current !== gaId) {
        ReactGA.initialize(gaId);

        gaInitKeyRef.current = gaId;
      }

      ReactGA.event({
        category: "Ecommerce",
        action: "Begin Checkout",
      });
    }
  }, [formData, store_slug, price_slug2]);

  // ============================================================
  // DOCUMENT TITLE
  // ============================================================

  useEffect(() => {
    const planName = String(formData?.name || "").trim();
    const teamName = String(formData?.team?.name || "").trim();

    if (planName && teamName) {
      document.title = `${planName} | ${teamName}`;
    } else {
      document.title = planName || teamName || "Piranha Profits";
    }
  }, [formData]);

  // ============================================================
  // DERIVED CHECKOUT SETTINGS
  // ============================================================

  const checkoutPage = formData?.checkout_page || {};

  const page = checkoutPage.page || {};
  const header = checkoutPage.header || {};
  const cta = checkoutPage.cta || {};
  const features = checkoutPage.features || {};
  const testimonial = checkoutPage.testimonial || {};
  const additionalContent = checkoutPage.additional_content || {};
  const subFooterContent = checkoutPage.sub_footer_content || {};

  const selectedLogo = getSelectedLogo(page?.logo);

  const selectedFontFamily = getSelectedFontFamily(page?.font_family);

  const visibleFeatures = (features.items || []).filter((feature) =>
    feature?.trim(),
  );

  // ============================================================
  // SUMMARY PRICE PLAN
  // ============================================================

  const summaryPlan = formData
    ? {
        ...formData,

        price_breakdown:
          pricingSnapshot?.price_breakdown ?? formData.price_breakdown ?? null,

        next_price_breakdown:
          pricingSnapshot?.next_price_breakdown ??
          formData.next_price_breakdown ??
          null,

        bundles: pricingSnapshot?.bundles ?? formData.bundles ?? [],

        coupons: pricingSnapshot?.coupons ?? formData.coupons ?? [],
      }
    : null;

  // ============================================================
  // FREE TRIAL WITH OR WITHOUT CREDIT CARD
  // ============================================================

  const parentCoupon = pickCouponForItem(summaryPlan, summaryPlan?.coupons);
  const isFreeTrial = Number(parentCoupon?.permission) === 8;

  const freeTrialNoCard =
    parentCoupon?.permission === 8 && !parentCoupon?.free_trial_is_cc_required;

  // ============================================================
  // LOAD GOOGLE FONT
  // ============================================================

  useEffect(() => {
    if (!selectedFontFamily?.googleFont) {
      return;
    }

    loadGoogleFont(selectedFontFamily.googleFont);
  }, [selectedFontFamily?.googleFont]);

  // ============================================================
  // LOADING
  // ============================================================

  if (isLoading) {
    return <Loading />;
  }

  // ============================================================
  // ERROR
  // ============================================================

  if (error || !formData) {
    return (
      <div className='container py-5'>
        <SanitizedHTML html={error} className='alert alert-danger' />
      </div>
    );
  }

  // ============================================================
  // DERIVED VALUES
  // ============================================================

  const pageCss = getPageCss(page);

  const featureSymbolStyle = getFeatureSymbolStyle(features?.symbol);

  const isCouponApplied =
    Boolean(appliedCouponCode) && couponCode === appliedCouponCode;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div
      className='checkout-content container-fluid min-vh-100 py-4'
      style={pageCss}
    >
      <div className='container'>
        {/* ================================================== */}
        {/* HERO */}
        {/* ================================================== */}

        <div className='mb-4'>
          {selectedLogo && (
            <img
              src={selectedLogo.src}
              alt={selectedLogo.label}
              className='checkout-logo'
            />
          )}

          <SanitizedHTML
            html={header.headline || ""}
            className='container-fluid'
          />
        </div>

        {/* ================================================== */}
        {/* CHECKOUT */}
        {/* ================================================== */}

        <div className='row justify-content-center g-3'>
          {/* ================================================== */}
          {/* PAYMENT FORM */}
          {/* ================================================== */}

          <div className='col-12 col-lg-7 order-2 order-lg-1'>
            <PaymentForm
              cta={cta}
              pricePlan={summaryPlan}
              store_slug={store_slug}
              price_slug={price_slug}
              geolocation={formData?.geolocation || null}
              onLocationChange={handleLocationChange}
              isRecalculating={isRecalculating || isApplyingCoupon}
              recalculationError={recalculationError}

              couponCode={appliedCouponCode}
              isFreeTrial={isFreeTrial}
              freeTrialNoCard={freeTrialNoCard}

              ef_transaction_id={everflowTrackingParams.ef_transaction_id}
              ef_oid={everflowTrackingParams.oid}
              ef_affid={everflowTrackingParams.affid}
            />
          </div>

          {/* ================================================== */}
          {/* ORDER SUMMARY */}
          {/* ================================================== */}

          <div className='col-12 col-lg-5 order-1 order-lg-2'>
            <div className='checkout-card shadow-sm bg-white'>
              <OrderSummary
                pricePlan={summaryPlan}
                freeTrialNoCard={freeTrialNoCard}
              />

              {/* ================================================== */}
              {/* FEATURES */}
              {/* ================================================== */}

              {visibleFeatures.length > 0 && (
                <div className='my-3'>
                  {features.title?.trim() && (
                    <div className='checkout-features-title'>
                      {features.title}
                    </div>
                  )}

                  <ul className='list-unstyled m-0 p-0'>
                    {visibleFeatures.map((feature, index) => (
                      <li
                        key={index}
                        className='checkout-feature-item mb-2 d-flex align-items-start gap-2'
                      >
                        <span
                          className={`checkout-tick ${
                            features.symbol?.shape === "none"
                              ? "checkout-tick-no-background"
                              : ""
                          }`}
                          style={featureSymbolStyle}
                        >
                          {features.symbol?.value || DEFAULT_FEATURE_SYMBOL}
                        </span>

                        <span className='checkout-feature-text'>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ================================================== */}
              {/* TESTIMONIAL */}
              {/* ================================================== */}

              {testimonial.body && (
                <SanitizedHTML
                  html={testimonial.body}
                  className='container-fluid my-3 p-2 border rounded'
                  style={{
                    backgroundColor:
                      testimonial.background_color ||
                      DEFAULT_TESTIMONIAL_BACKGROUND_COLOR,

                    "--bs-border-color":
                      testimonial.border_color ||
                      DEFAULT_TESTIMONIAL_BORDER_COLOR,
                  }}
                />
              )}

              {/* ================================================== */}
              {/* ADDITIONAL CONTENT */}
              {/* ================================================== */}

              {additionalContent.body && (
                <SanitizedHTML
                  html={additionalContent.body}
                  className='container-fluid my-3 p-2 border rounded'
                  style={{
                    backgroundColor:
                      additionalContent.background_color ||
                      DEFAULT_ADDITIONAL_CONTENT_BACKGROUND_COLOR,

                    "--bs-border-color":
                      additionalContent.border_color ||
                      DEFAULT_ADDITIONAL_CONTENT_BORDER_COLOR,
                  }}
                />
              )}

              {/* ================================================== */}
              {/* PRICE SUMMARY */}
              {/* ================================================== */}

              <PriceSummary
                pricePlan={summaryPlan}
                priceBreakdown={priceBreakdown}
                isRecalculating={isRecalculating || isApplyingCoupon}
                isFreeTrial={isFreeTrial}
              />

              {/* ================================================== */}
              {/* COUPON */}
              {/* ================================================== */}

              <div className='my-3 pt-3 border-top'>
                <label className='checkout-coupon-label d-block mb-2 fw-semibold text-uppercase'>
                  Have a coupon code?
                </label>

                <div className='d-flex align-items-stretch gap-2'>
                  <input
                    type='text'
                    className='checkout-input flex-grow-1'
                    placeholder='Enter coupon code'
                    value={couponCode}
                    disabled={isApplyingCoupon}
                    onChange={(event) => {
                      setCouponCode(normalizeCouponInput(event.target.value));

                      setCouponError("");
                      setCouponMessage("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") {
                        return;
                      }

                      event.preventDefault();

                      if (isApplyingCoupon || isRecalculating) {
                        return;
                      }

                      if (isCouponApplied) {
                        removeCoupon();
                      } else {
                        applyCoupon();
                      }
                    }}
                  />

                  {isCouponApplied ? (
                    <button
                      type='button'
                      className='btn btn-secondary checkout-coupon-apply'
                      onClick={removeCoupon}
                      disabled={isApplyingCoupon || isRecalculating}
                      title='Remove coupon'
                      aria-label='Remove coupon'
                    >
                      ✕
                    </button>
                  ) : (
                    <button
                      type='button'
                      className='btn btn-secondary checkout-coupon-apply'
                      onClick={applyCoupon}
                      disabled={
                        !couponCode.trim() ||
                        isApplyingCoupon ||
                        isRecalculating
                      }
                      title='Apply coupon'
                      aria-label='Apply coupon'
                    >
                      ✓
                    </button>
                  )}
                </div>

                {couponError && (
                  <div className='text-danger small fw-semibold mt-2'>
                    {couponError}
                  </div>
                )}

                {couponMessage && (
                  <div className='text-success small fw-semibold mt-2'>
                    {couponMessage}
                  </div>
                )}

                {appliedCouponCode && couponCode !== appliedCouponCode && (
                  <div className='text-muted small mt-2'>
                    Currently applied: <strong>{appliedCouponCode}</strong>
                    <button
                      type='button'
                      className='btn btn-link btn-sm text-danger ms-2 p-0'
                      onClick={removeCoupon}
                      disabled={isApplyingCoupon || isRecalculating}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* ================================================== */}
              {/* SUB-FOOTER CONTENT */}
              {/* ================================================== */}

              {subFooterContent.body && (
                <SanitizedHTML
                  html={subFooterContent.body}
                  className='container-fluid my-2 p-2 border rounded'
                  style={{
                    backgroundColor:
                      subFooterContent.background_color ||
                      DEFAULT_SUB_FOOTER_CONTENT_BACKGROUND_COLOR,

                    "--bs-border-color":
                      subFooterContent.border_color ||
                      DEFAULT_SUB_FOOTER_CONTENT_BORDER_COLOR,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
